/**
 * The typed Docsight API surface, grouped by domain and pointed at the REAL
 * backend routes (verified against the OpenAPI at :30300). Every method's
 * response type comes from {@link module:api/schema} — the generated mirror of
 * the backend contract — so a screen cannot invent a shape the server does not
 * return.
 *
 * This is the ONE data layer for the whole app: it is constructed once in
 * {@link module:auth/session} with the live bearer token and a single
 * `onUnauthorized` (drop to /login on a 401). Every screen reaches it through
 * `useSession().api` — there are no per-screen `ApiClient`s, so the auth-token
 * and 401 behaviour is uniform everywhere.
 */
import type { ApiClient, QueryValue } from './client';
import type {
  AnalyticsBreakdown,
  AnalyticsSummary,
  AnalyticsTimeseries,
  ApiKeysPage,
  Brand,
  CategoriesPage,
  Chat,
  Document,
  LocationsPage,
  NotificationsPage,
  Opportunity,
  OpportunityPage,
  Page,
  Principal,
  Profile,
  QueueHealth,
  ReviewPage,
  MailboxConnectRequest,
  MailboxConnectionView,
  MailboxCredentialsRequest,
  MailboxMessage,
  MailboxProbe,
  MailboxUpdateRequest,
  MailboxOutcome,
  ScannersPage,
  Shortlist,
  SuggestionsPage,
  UnreadCount,
  UsersPage,
} from './types';
import type {
  ApiKeyCreateRequest,
  ApiKeyView,
  ApprovalResponse,
  BrandingConfig,
  BrandingConfigUpdate,
  BrandingCreateRequest,
  BreakdownDimension,
  DesktopReleaseUpdate,
  DesktopReleaseView,
  BulkResolveRequest,
  BulkResolveResponse,
  Category,
  CategoryCreate,
  CategorySuggestion,
  CategoryUpdate,
  CategoryUpdateResult,
  DlqListResponse,
  DocumentFieldConfigUpdate,
  DocumentFieldVisibility,
  DocumentStatusView,
  DocumentView,
  EntityStatus,
  ErasureRequest,
  ErasureResponse,
  Granularity,
  IngestionSourceCreateRequest,
  IngestionSourceUpdateRequest,
  IngestionSourceView,
  IssuedApiKeyView,
  IssuedIngestionSourceView,
  IssuedWebhookEndpointView,
  Location,
  LocationCreate,
  LocationUpdate,
  MarkAllReadResponse,
  NotificationPreferenceView,
  NotificationView,
  OcrTextResponse,
  Page_CategorySuggestion_,
  Page_EffectiveCategory_,
  Page_Partner_,
  Page_ReviewQueueItem_,
  Page_TenantView_,
  Partner,
  PartnerAdminCreate,
  PartnerCreate,
  PartnerTenantsOverview,
  PartnerUpdate,
  PasswordChange,
  ProfileUpdate,
  PlatformConfigHistoryResponse,
  PlatformConfigUpdate,
  PlatformConfigView,
  PreferenceUpdateRequest,
  ReplayResult,
  ResolveRequest,
  ResolveResponse,
  RetentionHistoryEntry,
  Scanner,
  ScannerCreate,
  ScannerUpdate,
  SweepMode,
  SweepResponse,
  TenantProvisionRequest,
  TenantRetention,
  TenantRetentionUpdate,
  TenantUserInvite,
  TenantView,
  TurnOnResponse,
  User,
  UserUpdate,
  WebhookDeliveryView,
  WebhookEndpointCreateRequest,
  WebhookEndpointUpdateRequest,
  WebhookEndpointView,
} from './schema';

export interface PageQuery {
  skip?: number;
  limit?: number;
}

export interface AnalyticsFilter {
  start?: string;
  end?: string;
  type?: string;
  /** Backend query name is `location_id` (see the analytics OpenAPI). */
  location_id?: string;
  /** Backend query name is `scanner_id`. */
  scanner_id?: string;
  source?: string;
}

/** Query for the documents list endpoint (human param names; `*_id` at the edge). */
export interface ListDocumentsParams {
  skip: number;
  limit: number;
  status?: string;
  type?: string;
  location_id?: string;
  scanner_id?: string;
  source?: string;
  q?: string;
  start?: string;
  end?: string;
}

/** The FastAPI pagination envelope over documents. */
export type DocumentsPage = Page<DocumentView>;

/** Body for `POST /v1/documents/export` — the list filters, minus pagination. */
export interface ExportRequestBody {
  status?: string;
  type?: string;
  location_id?: string;
  scanner_id?: string;
  source?: string;
  q?: string;
  start?: string;
  end?: string;
}

/** An async CSV export job's status, as `POST`/`GET .../export{,/{id}}` report it. */
export interface ExportJobView {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'DONE' | 'FAILED';
  row_count: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

/** What `POST /v1/automation-opportunities/recompute` reports it just did. */
export interface RecomputeResult {
  count: number;
  computed_at: string;
}

/**
 * The capture metadata a submission MAY attach (the backend's
 * `DocumentCaptureInput`, sent as the multipart `metadata` part). Everything
 * else about a document — id, status, hashes, the source channel — is
 * server-controlled and cannot be named here.
 */
export interface DocumentCaptureBody {
  location_id?: string;
  scanner_id?: string;
  operator_user_ref?: string;
  source_id?: string;
  /** ISO-8601 instant the page was actually scanned, if known. */
  scanned_at?: string;
  custom_metadata?: Record<string, string>;
}

/**
 * A file upload gets its own deadline: the default 15s is a sensible ceiling for
 * a JSON round-trip but far too tight for tens of megabytes over a slow link.
 */
const UPLOAD_TIMEOUT_MS = 120_000;

/** Fetching a stored original is the same size problem as uploading one. */
const ORIGINAL_TIMEOUT_MS = 120_000;

/**
 * A chat turn is not a JSON round-trip: it is a scope-gate call, then a model
 * loop, then one or more aggregations, and the server retries a transient
 * upstream blip in-process. Measured turns against the real deployment land
 * between 6s and 14s, so the default 15s ceiling was aborting *successful*
 * answers — the request was still in flight when the client gave up, the user
 * was told the analytics could not be reached, and "Try again" then worked
 * because the next turn happened to be a second faster. 90s leaves room for a
 * slow turn plus a retried connect without ever racing a working answer.
 *
 * This bounds the client's patience only. Cost is bounded server-side by the
 * agent's per-turn token budget and the 30/min chat rate limit.
 */
const CHAT_TIMEOUT_MS = 90_000;

export interface QueueQuery {
  skip?: number;
  limit?: number;
  /** `age | age_desc | confidence | confidence_desc`. */
  sort?: string;
  /** A `ReviewReason`, omitted for "all". */
  reason?: string;
}

export interface PartnerListQuery {
  skip?: number;
  limit?: number;
  partner_status?: EntityStatus;
}

export interface TenantListQuery {
  skip?: number;
  limit?: number;
  tenant_status?: EntityStatus;
  partner_id?: string;
}

/** Credentials for email + password sign-in (POST /v1/auth/login). */
export interface LoginRequest {
  email: string;
  password: string;
}

/** The freshly minted session token the login endpoint returns. */
export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  /** Present only under AUTH_MODE=auth0 — send to `auth.refresh` to renew. */
  refresh_token?: string;
}

/** The three per-stage dead-letter queues (architecture §6.1, `dlq.py`). */
export const DLQ_STAGES = ['ocr', 'classify', 'reclassify'] as const;
export type DlqStage = (typeof DLQ_STAGES)[number];

/** Every row of a paginated collection the settings screens render in full. */
const SETTINGS_PAGE_LIMIT = 200;

/**
 * Widen a typed query object (an interface with optional string/number/boolean
 * fields — all valid `QueryValue`s) to the client's index-signature shape.
 */
const q = (params: object): Record<string, QueryValue> => params as Record<string, QueryValue>;

const cleanQueue = (query: QueueQuery): Record<string, QueryValue> => {
  const out: Record<string, QueryValue> = {};
  if (query.skip !== undefined) out.skip = query.skip;
  if (query.limit !== undefined) out.limit = query.limit;
  if (query.sort !== undefined) out.sort = query.sort;
  if (query.reason !== undefined) out.reason = query.reason;
  return out;
};

/** Builds the domain-grouped API from a configured {@link ApiClient}. */
export function createResources(client: ApiClient) {
  return {
    auth: {
      /** The authenticated principal (role + permissions come from the DB, not the token). */
      me: (signal?: AbortSignal): Promise<Principal> => client.get('/v1/auth/me', { signal }),
      /**
       * Exchange an email + password for a session token (unauthenticated). The
       * backend returns the SAME bearer the middleware validates; the caller then
       * adopts it via `signIn`. Unknown email, wrong password and a passwordless
       * account all reject with an identical 401 — never trust the body to tell
       * them apart.
       */
      login: (body: LoginRequest): Promise<LoginResponse> => client.post('/v1/auth/login', body),
      /**
       * Exchange a refresh token for a fresh access token (unauthenticated).
       * AUTH_MODE=auth0 only. An expired/revoked refresh token rejects with
       * the same 401 a wrong password does.
       */
      refresh: (refreshToken: string): Promise<LoginResponse> =>
        client.post('/v1/auth/refresh', { refresh_token: refreshToken }),
    },

    /**
     * The signed-in user's OWN account. Every route here is id-less by design:
     * the row acted on is whichever one the bearer token names, so there is no
     * parameter through which a caller could reach somebody else's profile.
     */
    me: {
      get: (signal?: AbortSignal): Promise<Profile> => client.get('/v1/me', { signal }),
      /** Rename yourself. A `role`/`status` in the body is a 422, never a silent drop. */
      update: (body: ProfileUpdate): Promise<Profile> => client.patch('/v1/me', body),
      /** Step up with the current password, then set a new one. 403 wrong / 422 policy. */
      changePassword: (body: PasswordChange): Promise<void> =>
        client.post('/v1/me/password', body),
      avatar: {
        /**
         * The avatar bytes as a `Blob`. Fetched through the client rather than
         * an `<img src>` because the endpoint needs the bearer token, which the
         * browser would not attach to an image request.
         */
        get: (signal?: AbortSignal): Promise<Blob> => client.getBlob('/v1/me/avatar', { signal }),
        upload: (file: File): Promise<Profile> => {
          const form = new FormData();
          form.append('file', file);
          // No explicit Content-Type: the browser has to set the multipart
          // boundary itself, and naming the type would strip it.
          return client.post('/v1/me/avatar', form);
        },
        remove: (): Promise<Profile> => client.delete('/v1/me/avatar'),
      },
    },

    branding: {
      /** Unauthenticated: resolve a hostname to its public brand (login pre-auth). */
      resolve: (hostname: string, signal?: AbortSignal): Promise<Brand> =>
        client.get('/v1/branding/resolve', { query: { hostname }, signal }),
      /**
       * The signed-in caller's own applied brand — by tenant/partner
       * ownership, not hostname. Needs no `*_branding:manage` grant; every
       * authenticated role reaches it.
       */
      mine: (signal?: AbortSignal): Promise<Brand> => client.get('/v1/branding/mine', { signal }),
      /** The caller's own logo/icon bytes, for an in-app preview (sidebar, sign-in card). */
      downloadMine: (kind: 'logo' | 'icon', signal?: AbortSignal): Promise<Blob> =>
        client.getBlob(`/v1/branding/mine/assets/${kind}`, { signal }),
      /** Unauthenticated: the resolved hostname's logo/icon bytes (login pre-auth). */
      downloadResolved: (hostname: string, kind: 'logo' | 'icon', signal?: AbortSignal): Promise<Blob> =>
        client.getBlob(`/v1/branding/resolve/assets/${kind}`, { query: { hostname }, signal }),
    },

    documentFields: {
      /**
       * Which Document Detail tabs/fields are hidden for the caller's own
       * tenant — by tenant/partner ownership. Needs no `partner_doc_fields:
       * manage` grant; every authenticated role reaches it.
       */
      mine: (signal?: AbortSignal): Promise<DocumentFieldVisibility> =>
        client.get('/v1/document-field-config/mine', { signal }),
    },

    notifications: {
      list: (
        query: PageQuery & { unread_only?: boolean } = {},
        signal?: AbortSignal,
      ): Promise<NotificationsPage> => client.get('/v1/notifications', { query: q(query), signal }),
      unreadCount: (signal?: AbortSignal): Promise<UnreadCount> =>
        client.get('/v1/notifications/unread-count', { signal }),
      markRead: (id: string): Promise<NotificationView> => client.post(`/v1/notifications/${id}/read`),
      markAllRead: (): Promise<MarkAllReadResponse> => client.post('/v1/notifications/read-all'),
      preferences: (signal?: AbortSignal): Promise<NotificationPreferenceView> =>
        client.get('/v1/notifications/preferences', { signal }),
      updatePreferences: (body: PreferenceUpdateRequest): Promise<NotificationPreferenceView> =>
        client.put('/v1/notifications/preferences', body),
    },

    review: {
      list: (query: PageQuery & { reason?: string } = {}, signal?: AbortSignal): Promise<ReviewPage> =>
        client.get('/v1/review', { query: q(query), signal }),
      /** Convenience for the nav badge: the open-item total. */
      openCount: async (signal?: AbortSignal): Promise<number> => {
        const page = await client.get<ReviewPage>('/v1/review', { query: { limit: 1 }, signal });
        return page.total;
      },
      listQueue: (query: QueueQuery = {}, signal?: AbortSignal): Promise<Page_ReviewQueueItem_> =>
        client.get('/v1/review', { query: cleanQueue(query), signal }),
      getDocument: (id: string, signal?: AbortSignal): Promise<DocumentView> =>
        client.get(`/v1/documents/${id}`, { signal }),
      listCategories: (signal?: AbortSignal): Promise<Page_EffectiveCategory_> =>
        client.get('/v1/categories', { signal }),
      /** Resolve one item. May reject with an `ApiError` of status 409 (already resolved). */
      resolve: (id: string, body: ResolveRequest): Promise<ResolveResponse> =>
        client.post(`/v1/review/${id}/resolve`, body),
      /** Bulk assign/dismiss a selected set; per-item conflicts come back in the body. */
      bulkResolve: (body: BulkResolveRequest): Promise<BulkResolveResponse> =>
        client.post('/v1/review/bulk-resolve', body),
      /** Preview (`apply:false`) or run (`apply:true`) a category's auto-review sweep. */
      sweep: (body: SweepMode): Promise<SweepResponse> => client.post('/v1/review/sweep', body),
    },

    analytics: {
      summary: (filter: AnalyticsFilter = {}, signal?: AbortSignal): Promise<AnalyticsSummary> =>
        client.get('/v1/analytics/summary', { query: q(filter), signal }),
      timeseries: (
        filter: AnalyticsFilter & { granularity?: Granularity } = {},
        signal?: AbortSignal,
      ): Promise<AnalyticsTimeseries> => client.get('/v1/analytics/timeseries', { query: q(filter), signal }),
      breakdown: (
        dimension: BreakdownDimension,
        filter: AnalyticsFilter = {},
        signal?: AbortSignal,
      ): Promise<AnalyticsBreakdown> =>
        client.get('/v1/analytics/breakdown', { query: q({ dimension, ...filter }), signal }),
    },

    documents: {
      /**
       * Submit one file for ingestion (multipart `POST /v1/documents`) — the
       * manual counterpart to a scanner posting with an API key. The source
       * channel is NOT ours to choose: the backend reads it off the credential,
       * so a browser session lands as `ui`. Pass `idempotencyKey` so a retry of
       * a submission that may already have landed answers `duplicate: true`
       * instead of ingesting the file twice.
       */
      upload: (
        file: File,
        capture: DocumentCaptureBody = {},
        idempotencyKey?: string,
      ): Promise<DocumentStatusView> => {
        const form = new FormData();
        // No explicit Content-Type: the browser must set the multipart boundary.
        form.append('file', file);
        if (Object.keys(capture).length > 0) form.append('metadata', JSON.stringify(capture));
        return client.post('/v1/documents', form, {
          timeoutMs: UPLOAD_TIMEOUT_MS,
          ...(idempotencyKey === undefined ? {} : { headers: { 'Idempotency-Key': idempotencyKey } }),
        });
      },
      get: (id: string, signal?: AbortSignal): Promise<Document> =>
        client.get(`/v1/documents/${id}`, { signal }),
      /** The stored OCR text for the detail view's OCR tab. 404 when none is stored yet. */
      ocrText: (id: string, signal?: AbortSignal): Promise<OcrTextResponse> =>
        client.get(`/v1/documents/${id}/ocr-text`, { signal }),
      /**
       * The document's stored original bytes, for the preview and the Download
       * button. Fetched through the client rather than pointed at from an `<img>`
       * or `<iframe src>` because the endpoint needs the bearer token, which the
       * browser would not attach to either — the caller turns the blob into an
       * object URL and is responsible for revoking it. 404 for a deleted document
       * or one whose blob has gone.
       */
      original: (id: string, signal?: AbortSignal): Promise<Blob> =>
        client.getBlob(`/v1/documents/${id}/original`, {
          // The default `image/*` would be a lie for the PDFs most documents are.
          headers: { Accept: '*/*' },
          signal,
          timeoutMs: ORIGINAL_TIMEOUT_MS,
        }),
      list: (params: ListDocumentsParams, signal?: AbortSignal): Promise<DocumentsPage> =>
        client.get('/v1/documents', { query: q(params), signal }),
      /**
       * Start an async CSV export of every document matching `body` (not one
       * page) — `202` with the new job's `PENDING` status; the requester is
       * emailed a link once it is ready.
       */
      export: (body: ExportRequestBody): Promise<ExportJobView> =>
        client.post('/v1/documents/export', body),
      /** Poll an export job's status. 404 for another tenant's id or one that never existed. */
      exportStatus: (exportId: string, signal?: AbortSignal): Promise<ExportJobView> =>
        client.get(`/v1/documents/export/${exportId}`, { signal }),
      /**
       * The finished export's CSV bytes — fetched through the client for the same
       * reason `original` is: the bearer token can't ride a bare `<a href>`. 404
       * until the job reaches `DONE`.
       */
      exportDownload: (exportId: string, signal?: AbortSignal): Promise<Blob> =>
        client.getBlob(`/v1/documents/export/${exportId}/download`, {
          headers: { Accept: 'text/csv' },
          signal,
        }),
      /** Re-run classification (publishes to `q.reclassify`). May 422 (no OCR) / 409 (terminal). */
      reclassify: (id: string): Promise<DocumentStatusView> =>
        client.post(`/v1/documents/${id}/reclassify`),
      /** Apply a human correction: set the document's category. */
      correct: (id: string, categoryId: string): Promise<DocumentView> =>
        client.post(`/v1/documents/${id}/classification`, { category_id: categoryId }),
      /** Route a document back to the review queue. */
      sendToReview: (id: string): Promise<DocumentStatusView> =>
        client.post(`/v1/documents/${id}/review`),
    },

    categories: {
      list: (signal?: AbortSignal): Promise<CategoriesPage> => client.get('/v1/categories', { signal }),
      suggestions: (signal?: AbortSignal): Promise<SuggestionsPage> =>
        client.get('/v1/category-suggestions', { signal }),
    },

    taxonomy: {
      listCategories: (signal?: AbortSignal): Promise<Page_EffectiveCategory_> =>
        client.get('/v1/categories', { signal }),
      listSuggestions: (signal?: AbortSignal): Promise<Page_CategorySuggestion_> =>
        client.get('/v1/category-suggestions', { signal }),
      createCategory: (body: CategoryCreate): Promise<Category> => client.post('/v1/categories', body),
      updateCategory: (id: string, body: CategoryUpdate): Promise<CategoryUpdateResult> =>
        client.patch(`/v1/categories/${id}`, body),
      approveSuggestion: (id: string): Promise<ApprovalResponse> =>
        client.post(`/v1/category-suggestions/${id}/approve`),
      rejectSuggestion: (id: string): Promise<CategorySuggestion> =>
        client.post(`/v1/category-suggestions/${id}/reject`),
      turnOnSuggestion: (id: string): Promise<TurnOnResponse> =>
        client.post(`/v1/category-suggestions/${id}/turn-on`),
      /** Preview (`apply: false`) or run (`apply: true`) the sweep for a category. */
      sweep: (body: SweepMode): Promise<SweepResponse> => client.post('/v1/review/sweep', body),
    },

    automation: {
      /**
       * `location`/`source` scope the server-side merge to one location or
       * ingestion channel. The endpoint returns one row per document type,
       * pooled across capture dimensions, so these have to be request
       * parameters rather than client-side filters.
       */
      list: (
        query: PageQuery & { location?: string; source?: string } = {},
        signal?: AbortSignal,
      ): Promise<OpportunityPage> =>
        client.get('/v1/automation-opportunities', { query: q(query), signal }),
      get: (id: string, signal?: AbortSignal): Promise<Opportunity> =>
        client.get(`/v1/automation-opportunities/${id}`, { signal }),
      /**
       * The shortlist the automation screen renders: document types worth
       * automating, each with the products that would do it and the pipeline
       * those products form.
       *
       * Recomputed server-side from the tenant's CURRENT documents on every
       * call, so this reflects a `recompute()` (or a nightly job run) without
       * any client-side cache to invalidate. Product matches come from the cache
       * that `match-products` refreshes; this call never waits on a model.
       */
      shortlist: (signal?: AbortSignal): Promise<Shortlist> =>
        client.get('/v1/automation-opportunities/shortlist', { signal }),
      /** Re-run the scoring job now. Capped tenant-wide at 3/day — a 429 means today's cap is spent. */
      recompute: (): Promise<RecomputeResult> =>
        client.post('/v1/automation-opportunities/recompute'),
    },

    chat: {
      ask: (message: string, conversationId?: string, signal?: AbortSignal): Promise<Chat> =>
        client.post(
          '/v1/chat',
          { message, conversation_id: conversationId },
          { signal, timeoutMs: CHAT_TIMEOUT_MS },
        ),
    },

    settings: {
      apiKeys: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<ApiKeysPage> =>
          client.get('/v1/api-keys', { query: { limit }, signal }),
        create: (body: ApiKeyCreateRequest): Promise<IssuedApiKeyView> => client.post('/v1/api-keys', body),
        rotate: (id: string): Promise<IssuedApiKeyView> => client.post(`/v1/api-keys/${id}/rotate`),
        revoke: (id: string): Promise<ApiKeyView> => client.post(`/v1/api-keys/${id}/revoke`),
      },
      sources: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<Page<IngestionSourceView>> =>
          client.get('/v1/ingestion-sources', { query: { limit }, signal }),
        create: (body: IngestionSourceCreateRequest): Promise<IssuedIngestionSourceView> =>
          client.post('/v1/ingestion-sources', body),
        update: (id: string, body: IngestionSourceUpdateRequest): Promise<IngestionSourceView> =>
          client.patch(`/v1/ingestion-sources/${id}`, body),
      },
      locations: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<LocationsPage> =>
          client.get('/v1/locations', { query: { limit }, signal }),
        create: (body: LocationCreate): Promise<Location> => client.post('/v1/locations', body),
        update: (id: string, body: LocationUpdate): Promise<Location> =>
          client.patch(`/v1/locations/${id}`, body),
      },
      mailboxes: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<Page<MailboxConnectionView>> =>
          client.get('/v1/mailboxes', { query: { limit }, signal }),
        get: (id: string, signal?: AbortSignal): Promise<MailboxConnectionView> =>
          client.get(`/v1/mailboxes/${id}`, { signal }),
        connect: (body: MailboxConnectRequest): Promise<MailboxConnectionView> =>
          client.post('/v1/mailboxes', body),
        update: (id: string, body: MailboxUpdateRequest): Promise<MailboxConnectionView> =>
          client.patch(`/v1/mailboxes/${id}`, body),
        replaceCredentials: (id: string, body: MailboxCredentialsRequest): Promise<MailboxConnectionView> =>
          client.put(`/v1/mailboxes/${id}/credentials`, body),
        disconnect: (id: string): Promise<void> => client.delete(`/v1/mailboxes/${id}`),
        test: (id: string, folder?: string): Promise<MailboxProbe> =>
          client.post(`/v1/mailboxes/${id}/test`, { folder: folder ?? null }),
        sync: (id: string): Promise<MailboxConnectionView> => client.post(`/v1/mailboxes/${id}/sync`),
        messages: (
          id: string,
          opts: { outcomes?: readonly MailboxOutcome[]; limit?: number } = {},
          signal?: AbortSignal,
        ): Promise<Page<MailboxMessage>> =>
          client.get(`/v1/mailboxes/${id}/messages`, {
            query: {
              limit: opts.limit ?? SETTINGS_PAGE_LIMIT,
              // Repeated as ?outcome=a&outcome=b when more than one applies.
              ...(opts.outcomes && opts.outcomes.length > 0 ? { outcome: opts.outcomes } : {}),
            },
            signal,
          }),
      },
      scanners: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<ScannersPage> =>
          client.get('/v1/scanners', { query: { limit }, signal }),
        unmapped: (signal?: AbortSignal): Promise<string[]> => client.get('/v1/scanners/unmapped', { signal }),
        create: (body: ScannerCreate): Promise<Scanner> => client.post('/v1/scanners', body),
        update: (id: string, body: ScannerUpdate): Promise<Scanner> =>
          client.patch(`/v1/scanners/${id}`, body),
      },
      retention: {
        get: (signal?: AbortSignal): Promise<TenantRetention> => client.get('/v1/retention', { signal }),
        update: (body: TenantRetentionUpdate): Promise<TenantRetention> =>
          client.patch('/v1/retention', body),
        history: (signal?: AbortSignal): Promise<RetentionHistoryEntry[]> =>
          client.get('/v1/retention/history', { signal }),
        erase: (body: ErasureRequest): Promise<ErasureResponse> => client.post('/v1/retention/erasure', body),
      },
      users: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<UsersPage> =>
          client.get('/v1/users', { query: { limit }, signal }),
        invite: (body: TenantUserInvite): Promise<User> => client.post('/v1/users', body),
        update: (id: string, body: UserUpdate): Promise<User> => client.patch(`/v1/users/${id}`, body),
        deactivate: (id: string): Promise<User> => client.delete(`/v1/users/${id}`),
      },
      branding: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<Page<BrandingConfig>> =>
          client.get('/v1/branding', { query: { limit }, signal }),
        create: (body: BrandingCreateRequest): Promise<BrandingConfig> => client.post('/v1/branding', body),
        update: (id: string, body: BrandingConfigUpdate): Promise<BrandingConfig> =>
          client.patch(`/v1/branding/${id}`, body),
        uploadAsset: (id: string, kind: 'logo' | 'icon', file: File): Promise<BrandingConfig> => {
          const form = new FormData();
          form.append('file', file);
          return client.post(`/v1/branding/${id}/assets/${kind}`, form);
        },
        /** The asset's bytes as a `Blob`, for an in-page preview (bearer-authed, unlike a bare `<img src>`). */
        downloadAsset: (id: string, kind: 'logo' | 'icon', signal?: AbortSignal): Promise<Blob> =>
          client.getBlob(`/v1/branding/${id}/assets/${kind}`, { signal }),
      },
      /** The published desktop uploader build, or `null` while nothing is live.
       * Open to any signed-in role — the admin's read is a different endpoint,
       * behind `platform_config:read`, which no tenant role holds. */
      desktopRelease: (signal?: AbortSignal): Promise<DesktopReleaseView | null> =>
        client.get('/v1/desktop-release/published', { signal }),
      webhooks: {
        list: (signal?: AbortSignal, limit = SETTINGS_PAGE_LIMIT): Promise<Page<WebhookEndpointView>> =>
          client.get('/v1/webhooks', { query: { limit }, signal }),
        create: (body: WebhookEndpointCreateRequest): Promise<IssuedWebhookEndpointView> =>
          client.post('/v1/webhooks', body),
        update: (id: string, body: WebhookEndpointUpdateRequest): Promise<WebhookEndpointView> =>
          client.patch(`/v1/webhooks/${id}`, body),
        rotate: (id: string): Promise<IssuedWebhookEndpointView> => client.post(`/v1/webhooks/${id}/rotate`),
        delete: (id: string): Promise<void> => client.delete(`/v1/webhooks/${id}`),
        /** One endpoint's delivery log, newest first — there is no cross-endpoint
         * feed on the backend, so a "recent deliveries" view fetches every
         * endpoint's log and merges client-side. */
        deliveries: (
          id: string,
          signal?: AbortSignal,
          limit = SETTINGS_PAGE_LIMIT,
        ): Promise<Page<WebhookDeliveryView>> =>
          client.get(`/v1/webhooks/${id}/deliveries`, { query: { limit }, signal }),
      },
    },

    admin: {
      partners: {
        list: (query: PartnerListQuery = {}, signal?: AbortSignal): Promise<Page_Partner_> =>
          client.get('/v1/partners', { query: q(query), signal }),
        get: (partnerId: string, signal?: AbortSignal): Promise<Partner> =>
          client.get(`/v1/partners/${partnerId}`, { signal }),
        create: (body: PartnerCreate): Promise<Partner> => client.post('/v1/partners', body),
        update: (partnerId: string, body: PartnerUpdate): Promise<Partner> =>
          client.patch(`/v1/partners/${partnerId}`, body),
        tenantsOverview: (partnerId: string, signal?: AbortSignal): Promise<PartnerTenantsOverview> =>
          client.get(`/v1/partners/${partnerId}/tenants-overview`, { signal }),
        /** A partner's `partner_admin` login accounts (never a password hash). */
        admins: {
          list: (partnerId: string, signal?: AbortSignal): Promise<User[]> =>
            client.get(`/v1/partners/${partnerId}/admins`, { signal }),
          /** Mint a `partner_admin` whose password is set here. 409 taken email / 422 weak. */
          create: (partnerId: string, body: PartnerAdminCreate): Promise<User> =>
            client.post(`/v1/partners/${partnerId}/admins`, body),
        },
      },
      documentFields: {
        /** Defaults (nothing hidden) rather than 404s when never configured. */
        get: (partnerId: string, signal?: AbortSignal): Promise<DocumentFieldVisibility> =>
          client.get(`/v1/document-field-config/${partnerId}`, { signal }),
        /** A whole-set replace — send the full current selection every time. */
        update: (partnerId: string, body: DocumentFieldConfigUpdate): Promise<DocumentFieldVisibility> =>
          client.put(`/v1/document-field-config/${partnerId}`, body),
      },
      tenants: {
        list: (query: TenantListQuery = {}, signal?: AbortSignal): Promise<Page_TenantView_> =>
          client.get('/v1/tenants', { query: q(query), signal }),
        create: (body: TenantProvisionRequest): Promise<TenantView> => client.post('/v1/tenants', body),
      },
      config: {
        active: (signal?: AbortSignal): Promise<PlatformConfigView> =>
          client.get('/v1/admin/config', { signal }),
        history: (signal?: AbortSignal): Promise<PlatformConfigHistoryResponse> =>
          client.get('/v1/admin/config/history', { signal }),
        update: (body: PlatformConfigUpdate): Promise<PlatformConfigView> =>
          client.put('/v1/admin/config', body),
        activate: (version: number): Promise<PlatformConfigView> =>
          client.post(`/v1/admin/config/activate/${String(version)}`),
      },
      /** The desktop uploader build every tenant is offered — one record for the
       * whole platform, so no id. `get` answers an empty record (never a 404)
       * before a first save; `save` is a whole-record replace. */
      desktopRelease: {
        get: (signal?: AbortSignal): Promise<DesktopReleaseView> =>
          client.get('/v1/desktop-release', { signal }),
        save: (body: DesktopReleaseUpdate): Promise<DesktopReleaseView> =>
          client.put('/v1/desktop-release', body),
      },
      health: (signal?: AbortSignal): Promise<QueueHealth> => client.get('/v1/admin/health', { signal }),
      dlq: {
        list: (stage: DlqStage, signal?: AbortSignal): Promise<DlqListResponse> =>
          client.get('/v1/admin/dlq', { query: { stage }, signal }),
        replay: (stage: string, messageId: string): Promise<ReplayResult> =>
          client.post(`/v1/admin/dlq/${stage}/${messageId}/replay`),
        discard: (stage: string, messageId: string): Promise<ReplayResult> =>
          client.post(`/v1/admin/dlq/${stage}/${messageId}/discard`),
      },
    },
  };
}

export type Resources = ReturnType<typeof createResources>;

/** The admin console's typed API — the `admin` slice of the shared resources. */
export type AdminApi = Resources['admin'];
/** The review domain's typed API — the `review` slice of the shared resources. */
export type ReviewApi = Resources['review'];
/** The taxonomy screen's typed API — the `taxonomy` slice of the shared resources. */
export type TaxonomyApi = Resources['taxonomy'];
/** The documents screen's typed API — the `documents` slice of the shared resources. */
export type DocumentsApi = Resources['documents'];
/** The notifications panel's typed API — the `notifications` slice of the shared resources. */
export type NotifApi = Resources['notifications'];
/** The settings screens' typed API — the `settings` slice of the shared resources. */
export type SettingsApi = Resources['settings'];
